// CanvasAsset.tsx
import React, { useCallback } from "react";
import { CanvasComponentProps, CanvasEditableProps } from "./sharedProps";
import {
  AnyObject,
  Box,
  Item,
  SaveDataBuilders,
  TextEditor,
  useThemeColor,
} from "@thenounproject/lingo-core";
import { getVersionedItemId } from "@src/helpers/items";
import useModals, { ModalTypes } from "@actions/useModals";

import { ItemPolling } from "../GalleryAssetContent/PollingWrappers";
import GalleryAssetWrapper from "../GalleryAssetContent/GalleryAssetWrapper";
import { getCanvasLayoutProperties } from "./utils";
import { SelectionWrapper } from "./SelectionWrapper";
import { CanvasItemWrapper } from "./CanvasItemWrapper";

type Props = {
  onUseButtonSelected: (item: Item, e: React.MouseEvent) => void;
  containerWidth: number;
} & CanvasComponentProps &
  CanvasEditableProps;

const CanvasAsset: React.FC<Props> = ({
  item,
  isSelected,
  onClick,
  onDoubleClick,
  onUseButtonSelected,
  onOverflowSelected,
  draggable,
  onDragEnd,
  onDragStart,
  canEdit,
  canGenerateText,
  index,
  isEditing,
  onBeginEditing,
  onFinishEditing,
  containerWidth,
  ...rest
}) => {
  const versionedId = getVersionedItemId(item);
  const { size, isSquare, outerWidth, innerThumbnailWidth } = getCanvasLayoutProperties({
    item,
    containerWidth,
  });

  const layout = isSquare ? "square" : "full";
  const caption = item?.displayProperties?.caption;
  const downloadable = item?.displayProperties?.allowDownload;
  const canInspect = downloadable || canEdit;
  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick(item, e);
    },
    [item, onClick]
  );
  const _onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-testid="tiptap-editor"]')) {
        return;
      }
      onDoubleClick(item, e);
    },
    [item, onDoubleClick]
  );

  const handleFinishEditing = useCallback(
    (data: AnyObject) => {
      onFinishEditing(item, data);
    },
    [item, onFinishEditing]
  );

  const { showModal } = useModals();
  const handleAIGenerate = useCallback(() => {
    showModal(ModalTypes.GENERATE_TEXT_CONTENT, {
      item,
      buildSaveData: SaveDataBuilders.caption,
    });
  }, [showModal, item]);

  const backgroundColor = useThemeColor(item.displayProperties.cardBackgroundColor);

  const renderCaption = () => {
    if (!caption) return null;
    return (
      <TextEditor
        data-focus-id={`caption-${item.id}`}
        itemId={item.id}
        content={caption}
        buildSaveData={SaveDataBuilders.caption}
        isEditing={isEditing}
        canEdit={canEdit}
        selected={isSelected}
        onBeginEditing={onBeginEditing}
        onFinishEditing={handleFinishEditing}
        hasDefaultValue={Boolean(caption)}
        backgroundColor={item.displayProperties.cardBackgroundColor}
        onAIGenerate={canGenerateText ? handleAIGenerate : undefined}
        p={"0px"}
      />
    );
  };

  return (
    <CanvasItemWrapper
      data-full-width={outerWidth === "100%" || size === 1}
      outerWidth={outerWidth}
      size={size}
      index={index}
      itemId={item.id}
      {...rest}>
      <ItemPolling item={item} />
      <Box
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={canInspect ? _onClick : undefined}
        onDoubleClick={canInspect ? _onDoubleClick : undefined}
        position="relative">
        <SelectionWrapper selected={isSelected}>
          <GalleryAssetWrapper
            item={item}
            layout={layout}
            canEdit={canEdit}
            thumbnailWidth={innerThumbnailWidth}
            onUseButtonSelected={onUseButtonSelected}
            onOverflowSelected={onOverflowSelected}
            backgroundColor={backgroundColor}
            isSelected={isSelected}>
            {renderCaption()}
          </GalleryAssetWrapper>
        </SelectionWrapper>
      </Box>
    </CanvasItemWrapper>
  );
};

export default CanvasAsset;
